import React from 'react';
import { ArrowPathIcon, BanknotesIcon, SparklesIcon, StarIcon } from '@heroicons/react/24/outline';
import { InspoHouse, InspoPlaybook, InspoStory } from '../../../services';
import { RisingCard } from './StoryCards';

type Tile = { icon: React.ElementType; value: string; label: string };

// #region tilesOf
const tilesOf = (p: InspoPlaybook): Tile[] => {
    const tiles: Tile[] = [];
    if (p.hero_share_pct) tiles.push({ icon: StarIcon, value: `${p.hero_share_pct}%`, label: 'of a typical top brand’s orders come from just its 3 best designs. Find yours, then make more like them.' });
    if (p.repeat_rate_range) tiles.push({ icon: ArrowPathIcon, value: `${p.repeat_rate_range[0]}–${p.repeat_rate_range[1]}%`, label: 'of their customers come back and order again.' });
    if (p.top_motif) tiles.push({ icon: SparklesIcon, value: p.top_motif.replace(/^./, (c) => c.toUpperCase()), label: 'is the style behind most of their best sellers.' });
    if (p.typical_kept_pct) tiles.push({ icon: BanknotesIcon, value: `${p.typical_kept_pct}%+`, label: 'of each sale is what top brands keep after Wallmates production and shipping, before their own Shopify and marketing costs.' });
    return tiles;
};

// #region Benchmark
/** What the top-selling brands share, from their numbers. Anonymous by design: it describes all of them at once. */
export const Benchmark: React.FunctionComponent<{ playbook: InspoPlaybook; rising: InspoStory | null }> = ({ playbook, rising }) => {
    const tiles = tilesOf(playbook);
    if (!tiles.length && !rising) return null;
    return (
        <section className="space-y-4">
            <div>
                <h2 className="font-display text-3xl font-semibold tracking-tight text-black dark:text-white">What the top brands have in common</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Across the 12 brands selling the most on Wallmates over the last 90 days.</p>
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
                <div className="grid grid-cols-2 gap-3 lg:col-span-2">
                    {tiles.map((t) => {
                        const Icon = t.icon;
                        return (
                            <div key={t.value} className="rounded-2xl border border-gray-200 bg-surface-alt p-4 dark:border-stone-700">
                                <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                                <p className="mt-3 font-display text-3xl font-semibold tabular-nums text-black sm:text-4xl dark:text-white">{t.value}</p>
                                <p className="mt-1 text-xs leading-relaxed text-gray-600 sm:text-sm dark:text-gray-300">{t.label}</p>
                            </div>
                        );
                    })}
                </div>
                {rising && <RisingCard story={rising} />}
            </div>
        </section>
    );
};

/** The Wall Blush wordmark, served from Wall Blush's own Shopify store (transparent PNG). */
const WALL_BLUSH_WORDMARK = 'https://cdn.shopify.com/s/files/1/0108/8225/2863/files/Wall-Blush-Logo_aab661d9-8047-4b60-ac33-c1afc232c5d0.png?width=600';

// #region FoundersStory
/** Our own brand, Wall Blush, runs on the same system. Counts only, never revenue. */
export const FoundersStory: React.FunctionComponent<{ house: InspoHouse }> = ({ house }) => (
    <section className="flex flex-col gap-5 rounded-3xl border border-gray-200 bg-surface-alt p-5 sm:flex-row sm:items-center sm:p-8 dark:border-stone-700">
        {/* Wall Blush's logo: its real wordmark (from wallblush.com) on its tan square. */}
        <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-2xl bg-[#e4b678] sm:h-32 sm:w-32">
            <img src={WALL_BLUSH_WORDMARK} alt={`${house.name} logo`} className="w-[78%]" />
        </div>
        <div className="flex-1 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand">Our own brand</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-black dark:text-white">{house.name} runs on Wallmates too</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">The same printing, packing and shipping you get handles every {house.name} order.</p>
        </div>
        <dl className="grid grid-cols-2 gap-3 sm:w-80">
            <div className="rounded-xl bg-gray-50 px-3 py-2.5 dark:bg-stone-800/70">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Customers</dt>
                <dd className="text-xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">{house.customers}</dd>
            </div>
            <div className="rounded-xl bg-gray-50 px-3 py-2.5 dark:bg-stone-800/70">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Orders shipped</dt>
                <dd className="text-xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">{house.orders}</dd>
            </div>
        </dl>
    </section>
);
